import { refreshAuthorizationHeader } from '$shared/auth/auth.utils';
import { fetcher } from '$shared/fetch';
import { SUPPORT_CHAT_PATH, SUPPORT_CHAT_FEEDBACK_PATH } from '$shared/utils/api-paths';

const CHAT_API_BASE_URL = import.meta.env.PUBLIC_CHAT_API_URL || '';

// Chat API service for communicating with the backend
export interface ChatAPIMessage {
  question: string;
  sessionId: string | undefined;
}

export interface ChatAPIResponse {
  response: string;
  sessionId: string;
  messageId: string;
}

export interface ChatFeedbackPayload {
  sessionId: string;
  messageId: string;
  feedback: 'up' | 'down' | null;
}

export interface StreamCallbacks {
  onToken: (token: string) => void;
  onDone: (sessionId: string, messageId: string) => void;
  onError: (error: string) => void;
}

export class ChatService {
  private sessionId: string | undefined = undefined;

  async sendMessage(message: string): Promise<ChatAPIResponse> {
    try {
      const response = await fetcher.post<ChatAPIMessage, ChatAPIResponse>(
        SUPPORT_CHAT_PATH,
        {
          question: message,
          sessionId: this.sessionId,
        },
        { timeout: 120000 },
      );
      const chatResponseData: ChatAPIResponse = response.data;
      this.sessionId = chatResponseData.sessionId;

      return chatResponseData;
    } catch (error) {
      throw new Error('Failed to send message to chat service');
    }
  }

  async sendMessageStreaming(message: string, callbacks: StreamCallbacks): Promise<void> {
    const url = `${CHAT_API_BASE_URL}${SUPPORT_CHAT_PATH}`;
    const authHeader = await refreshAuthorizationHeader();

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authHeader ? { Authorization: authHeader } : {}),
      },
      body: JSON.stringify({
        question: message,
        sessionId: this.sessionId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('No response body');
    }

    const decoder = new TextDecoder();
    let buffer = '';

    try {
      // eslint-disable-next-line no-constant-condition -- streaming requires continuous reads until done
      while (true) {
        // eslint-disable-next-line no-await-in-loop -- sequential stream reads cannot be parallelized
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        lines
          .filter((line) => line.startsWith('data: '))
          .forEach((line) => {
            const json = line.slice(6);
            try {
              const event = JSON.parse(json);

              if (event.type === 'text') {
                callbacks.onToken(event.content);
              } else if (event.type === 'done') {
                this.sessionId = event.sessionId;
                callbacks.onDone(event.sessionId, event.messageId);
              } else if (event.type === 'error') {
                callbacks.onError(event.content);
              }
            } catch {
              // skip malformed JSON
            }
          });
      }
    } finally {
      reader.releaseLock();
    }
  }

  async submitFeedback(messageId: string, feedback: 'up' | 'down' | null): Promise<void> {
    if (!this.sessionId) return;
    try {
      const url = `${CHAT_API_BASE_URL}${SUPPORT_CHAT_FEEDBACK_PATH}`;
      const authHeader = await refreshAuthorizationHeader();
      const payload: ChatFeedbackPayload = {
        sessionId: this.sessionId,
        messageId,
        feedback,
      };
      await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(authHeader ? { Authorization: authHeader } : {}),
        },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      // Feedback submission failures are non-critical, log and continue
      // eslint-disable-next-line no-console -- Non-critical feedback error
      console.error('Failed to submit chat feedback:', error);
    }
  }

  resetSession(): void {
    this.sessionId = undefined;
  }

  getSessionId(): string | undefined {
    return this.sessionId;
  }
}
